/**
 * StepsManager — GPX steps and track rendering
 */

import {fetchGpxAsLineString} from "../utils/gpx-parser.js"
import {calculateBounds, calculateGlobalBounds} from "../utils/bounds-calculator.js"

const CONFIG = {
    STEP_CARD: "[data-step-card]",
}

export default class StepsManager {
    constructor(mapManager, block) {
        this.mapManager = mapManager
        this.map = mapManager.getMap()
        this.block = block
        this.allSteps = new Map() // All loaded GPX tracks
        this.globalBounds = [] // Coordinate arrays used for global fitBounds
        this.selectedStepId = null // Currently highlighted step id (single selection)
    }

    /**
     * Loads every GPX track as the default black line
     */
    async loadAllSteps() {
        const filterOptions = this.block.querySelectorAll("[data-json]")

        if (!filterOptions || filterOptions.length === 0) {
            console.warn("No GPX steps found")
            return
        }

        const loadPromises = []
        const processedIds = new Set()

        for (let i = 0; i < filterOptions.length; i++) {
            const option = filterOptions[i]
            try {
                const data = JSON.parse(option.dataset.json)
                if (data?.gpx?.url && data?.gpx?.options?.id) {
                    const stepId = data.gpx.options.id

                    // Avoid loading the same track twice
                    if (!processedIds.has(stepId)) {
                        processedIds.add(stepId)
                        loadPromises.push(this.loadSingleStep(data))
                    }
                }
            } catch (error) {
                console.error("Error parsing step data:", error)
            }
        }

        await Promise.all(loadPromises)

        // Fit the map to show every track
        if (this.globalBounds.length > 0) {
            this.fitGlobalBounds()
        }
    }

    /**
     * Loads one GPX track (GeoJSON line)
     */
    async loadSingleStep(data) {
        try {
            const geojson = await fetchGpxAsLineString(data.gpx.url)
            const stepId = data.gpx.options.id || "step"

            // Persist step payload + GeoJSON
            this.allSteps.set(stepId, {
                geojson: geojson,
                data: {
                    title: data.title || geojson.properties.name,
                    tags: data.tags || [],
                    url: data.url || "#",
                    bounds: geojson.geometry.coordinates,
                },
            })

            // Feed global bounding calculation
            this.globalBounds.push(geojson.geometry.coordinates)

            // Draw default (black) track on the map
            this.addBlackLayer(stepId, geojson)

            // Pre-create blue highlight layers (hidden until selection)
            this.preloadBlueLayer(stepId)
        } catch (error) {
            console.error(`Error loading step ${data.gpx?.options?.id}:`, error)
        }
    }

    /**
     * Returns the POI cluster anchor layer when it exists.
     */
    getPoiClusterBeforeId() {
        return this.map.getLayer("poi-cluster") ? "poi-cluster" : undefined
    }

    /**
     * Adds the default black track line + invisible wide hit area
     */
    addBlackLayer(stepId, geojson) {
        const sourceId = `step-${stepId}`
        const layerIdLine = `step-${stepId}-line-black`
        const layerIdHover = `step-${stepId}-line-black-hover`

        // Skip if source already registered
        if (this.map.getSource(sourceId)) {
            console.warn(`Source ${sourceId} already exists, skipping`)
            return
        }

        // Register GeoJSON source
        this.map.addSource(sourceId, {
            type: "geojson",
            data: geojson,
        })

        // Black line layer (always visible)
        this.map.addLayer(
            {
                id: layerIdLine,
                type: "line",
                source: sourceId,
                layout: {
                    "line-join": "round",
                    "line-cap": "round",
                },
                paint: {
                    "line-color": "#000000",
                    "line-width": 3,
                },
            },
            this.getPoiClusterBeforeId()
        )

        // Invisible thick line for hover/click hit testing
        this.map.addLayer({
            id: layerIdHover,
            type: "line",
            source: sourceId,
            layout: {
                "line-join": "round",
                "line-cap": "round",
            },
            paint: {
                "line-color": "#000000",
                "line-width": 60,
                "line-opacity": 0,
            },
        })

        // Wire hover + click on the black track
        this.setupBlackLayerEvents(stepId, layerIdLine, layerIdHover)

        // Endpoint decorations (start/end)
        this.addEndpointMarkers(stepId, geojson)
    }

    /**
     * Adds decorative markers at the track endpoints
     */
    addEndpointMarkers(stepId, geojson) {
        const coordinates = geojson.geometry.coordinates

        if (!coordinates || coordinates.length < 2) {
            console.warn(`Not enough coordinates for step ${stepId}`)
            return
        }

        // Start / end coordinates
        const startCoord = coordinates[0]
        const endCoord = coordinates[coordinates.length - 1]

        // Icon URLs from global IRIS config
        const markerConfig = `
            width: 36px;
            height: 36px;
            background-image: url('${IRIS.assets.icons.flag}');
            background-size: contain;
            background-repeat: no-repeat;
            background-position: center;
            pointer-events: none;
        `

        // Start marker element
        const startMarkerEl = document.createElement("div")
        startMarkerEl.className = "step-endpoint-marker step-endpoint-marker--start"
        startMarkerEl.style = markerConfig

        // End marker element
        const endMarkerEl = document.createElement("div")
        endMarkerEl.className = "step-endpoint-marker step-endpoint-marker--end"
        endMarkerEl.style = markerConfig

        // Place markers via IrisMap API
        const startMarker = this.map
            .Marker({element: startMarkerEl})
            .setLngLat({lng: startCoord[0], lat: startCoord[1]})
            .addTo(this.map)

        const endMarker = this.map
            .Marker({element: endMarkerEl})
            .setLngLat({lng: endCoord[0], lat: endCoord[1]})
            .addTo(this.map)

        // Remember markers for selection styling
        if (!this.endpointMarkers) {
            this.endpointMarkers = new Map()
        }

        this.endpointMarkers.set(stepId, {
            start: startMarker,
            end: endMarker,
        })
    }

    /**
     * Updates endpoint marker icons when a step is selected
     */
    updateEndpointMarkers(stepId, isActive) {
        const markers = this.endpointMarkers?.get(stepId)
        if (!markers) return

        const icons = IRIS.assets.icons

        markers.start._element.style.backgroundImage = `url('${isActive ? icons.start : icons.flag}')`
        markers.end._element.style.backgroundImage = `url('${isActive ? icons.end : icons.flag}')`
        markers.end._element.style.zIndex = `${isActive ? 1 : 0}`
        markers.start._element.style.zIndex = `${isActive ? 1 : 0}`
    }

    /**
     * Attaches pointer interactions to the black track layers
     */
    setupBlackLayerEvents(stepId, layerIdLine, layerIdHover) {
        const canvas = this.map.getCanvas()
        let isHovering = false

        // Pointer hover feedback on the invisible hit layer
        this.map.on("mousemove", (e) => {
            try {
                const features = this.map.queryRenderedFeatures(e.point, {
                    layers: [layerIdHover],
                })

                const shouldHover = features.length > 0

                if (shouldHover && !isHovering) {
                    isHovering = true
                    canvas.style.cursor = "pointer"
                } else if (!shouldHover && isHovering) {
                    isHovering = false
                    canvas.style.cursor = ""
                }
            } catch (error) {
                console.error("Error in hover detection:", error)
            }
        })

        // Click uses the wide invisible layer for easier targeting
        this.map.on("click", (e) => {
            try {
                const features = this.map.queryRenderedFeatures(e.point, {
                    layers: [layerIdHover],
                })

                if (features.length > 0) {
                    this.selectStep(stepId)
                }
            } catch (error) {
                console.error("Error in click handler:", error)
            }
        })
    }

    /**
     * Selects a track (blue highlight + zoom + card)
     */
    async selectStep(stepId) {
        // Deselect any previously selected step
        if (this.selectedStepId) {
            this.deselectStep(this.selectedStepId)
        }

        // Resolve stored step data
        const stepData = this.allSteps.get(stepId)
        if (!stepData) {
            console.warn(`Step ${stepId} not found`)
            return
        }

        // Show blue highlight layers
        this.addBlueLayer(stepId)
        this.selectedStepId = stepId

        // Style endpoint markers for the active step
        this.updateEndpointMarkers(stepId, true)

        // Open the step info card
        this.showStepCard(stepId)

        // Fit bounds around the selected track
        this.fitStepBounds(stepData.data.bounds)

        this.block.dispatchEvent(new CustomEvent("select-step", {detail: {stepId}}))

        return stepData.data
    }

    /**
     * Pre-builds blue highlight layers (hidden until `addBlueLayer`)
     */
    preloadBlueLayer(stepId) {
        const sourceId = `step-${stepId}`
        const layerIdLine = `step-${stepId}-line-blue`
        const layerIdHover = `step-${stepId}-hover`

        // Source must exist from `addBlackLayer`
        if (!this.map.getSource(sourceId)) {
            console.error(`Source ${sourceId} not found, cannot preload blue layer`)
            return
        }

        // Blue line on top of black, initially hidden, inserted before POI cluster anchor
        this.map.addLayer(
            {
                id: layerIdLine,
                type: "line",
                source: sourceId,
                layout: {
                    "line-join": "round",
                    "line-cap": "round",
                    visibility: "none", // Hidden until selection
                },
                paint: {
                    "line-color": "#3182ce",
                    "line-width": 4,
                },
            },
            this.getPoiClusterBeforeId()
        )

        // Wide invisible companion layer for the blue track (hidden until selection)
        this.map.addLayer(
            {
                id: layerIdHover,
                type: "line",
                source: sourceId,
                layout: {
                    "line-join": "round",
                    "line-cap": "round",
                    visibility: "none", // Hidden until selection
                },
                paint: {
                    "line-color": "#3182ce",
                    "line-width": 60,
                    "line-opacity": 0,
                },
            },
            this.getPoiClusterBeforeId()
        )
    }

    /**
     * Reveals the preloaded blue layers and fixes stacking vs POI clusters
     */
    addBlueLayer(stepId) {
        const layerIdLine = `step-${stepId}-line-blue`
        const layerIdHover = `step-${stepId}-hover`

        if (this.map.getLayer(layerIdLine)) {
            this.map.setLayoutProperty(layerIdLine, "visibility", "visible")
            // 1. Find the lowest POI-related layer currently on the map
            //    (IDs created by MapManager cluster helpers)
            const poiAnchor = ["layer-poi-cluster", "unclustered-point-poi-cluster", "cluster-count-poi-cluster"].find(
                (id) => this.map.getLayer(id)
            )
            try {
                if (poiAnchor) {
                    // Keep highlight beneath POI layers when they exist
                    this.map.moveLayer(layerIdLine, poiAnchor)
                    if (this.map.getLayer(layerIdHover)) this.map.moveLayer(layerIdHover, poiAnchor)
                } else {
                    // No POI layers yet — move on top; new POI layers will stack above later.
                    this.map.moveLayer(layerIdLine)
                    if (this.map.getLayer(layerIdHover)) this.map.moveLayer(layerIdHover)
                }
            } catch (e) {
                console.warn("StepsManager: failed to reorder highlight layers", e)
                this.map.moveLayer(layerIdLine)
            }
            if (this.map.getLayer(layerIdHover)) {
                this.map.setLayoutProperty(layerIdHover, "visibility", "visible")
            }
        }
    }

    /**
     * Hides the blue highlight layers for a step
     */
    hideBlueLayer(stepId) {
        const layerIdLine = `step-${stepId}-line-blue`
        const layerIdHover = `step-${stepId}-hover`

        if (this.map.getLayer(layerIdLine)) {
            this.map.setLayoutProperty(layerIdLine, "visibility", "none")
        }
        if (this.map.getLayer(layerIdHover)) {
            this.map.setLayoutProperty(layerIdHover, "visibility", "none")
        }
    }

    /**
     * Clears selection state for a step id
     */
    deselectStep(stepId) {
        if (this.selectedStepId !== stepId) return

        this.hideStepInfo(stepId)
        this.hideBlueLayer(stepId)
        this.updateEndpointMarkers(stepId, false)
        this.selectedStepId = null
    }

    /**
     * Clears every selection and recenters on all tracks
     */
    resetAll() {
        // Drop current selection if any
        if (this.selectedStepId) {
            this.hideBlueLayer(this.selectedStepId)
            this.updateEndpointMarkers(this.selectedStepId, false)
            this.selectedStepId = null
        }

        this.hideStepInfo()
        this.fitGlobalBounds()
    }

    /**
     * Fits the map to one track's coordinate bounds
     */
    fitStepBounds(coordinates) {
        const bounds = calculateBounds(coordinates)
        if (!bounds) {
            console.error("Invalid bounds calculated for step")
            return
        }

        const {minLon, minLat, maxLon, maxLat} = bounds

        this.map.fitBounds(
            [
                {lng: minLon, lat: minLat},
                {lng: maxLon, lat: maxLat},
            ],
            {
                padding: {top: 50, bottom: 120, left: 50, right: 50},
                duration: 1000,
                maxZoom: 9,
            }
        )
    }

    /**
     * Fits the map to every loaded track
     */
    fitGlobalBounds() {
        const bounds = calculateGlobalBounds(this.globalBounds)
        if (!bounds) {
            console.error("Invalid global bounds calculated")
            return
        }

        const {minLon, minLat, maxLon, maxLat} = bounds

        this.map.fitBounds(
            [
                {lng: minLon, lat: minLat},
                {lng: maxLon, lat: maxLat},
            ],
            {
                padding: {top: 50, bottom: 120, left: 50, right: 50},
                duration: 1000,
            }
        )
    }

    /**
     * Returns the currently selected step for UI chips
     *
     * @returns {{id: string, title: string}|null}
     */
    getSelectedStep() {
        if (!this.selectedStepId) return null

        const stepData = this.allSteps.get(this.selectedStepId)
        if (!stepData) return null

        return {
            id: this.selectedStepId,
            title: stepData.data.title,
        }
    }

    showStepCard(id) {
        // Hide every step card
        const stepCards = this.block.querySelectorAll(CONFIG.STEP_CARD)
        for (let i = 0; i < stepCards.length; i++) {
            $(stepCards[i]).removeClass("is-active")
        }

        // Activate the selected step card
        const stepCard = $(`[data-step-card="${id}"]`)
        if (!stepCard) return

        $(stepCard).addClass("is-active")
    }

    hideStepInfo(id = null) {
        if (id) {
            // Hide one step card
            const stepCard = this.block.querySelector(`[data-step-card="${id}"]`)
            if (stepCard) {
                stepCard.classList.remove("is-active")
            }
        } else {
            // Hide every step card
            const stepCards = this.block.querySelectorAll(CONFIG.STEP_CARD)
            for (let i = 0; i < stepCards.length; i++) {
                stepCards[i].classList.remove("is-active")
            }
        }
    }
}
